import { useEffect, useState, useCallback } from 'react';
import api from '../api';
import Layout from '../components/Layout';
import { useToast } from '../components/ToastContext';
import { useConfirm } from '../components/ConfirmModal';
import { T } from '../styles/theme';

export default function GestioneVinted() {
  const [connected, setConnected] = useState(false);
  const [vintedUsername, setVintedUsername] = useState('');
  const [tokenInput, setTokenInput] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    api.get('/vinted/status').then((r) => {
      if (r.data.connected) { setConnected(true); setVintedUsername(r.data.username); }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadItems = useCallback(() => {
    if (!connected || tokenExpired) return;
    setLoading(true);
    api.get('/vinted/items', { params: { page, per_page: perPage } })
      .then((r) => {
        setItems(r.data.items || []);
        setTotalPages(r.data.total_pages || 1);
        setTotalItems(r.data.total || 0);
      })
      .catch((err) => {
        const s = err.response?.status;
        if (s === 401 || s === 500) setTokenExpired(true);
        else toast.error(err.response?.data?.detail || 'Errore caricamento');
      })
      .finally(() => setLoading(false));
  }, [connected, tokenExpired, page, perPage, toast]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleConnect = async () => {
    if (!tokenInput.trim()) { toast.error('Inserisci il session token'); return; }
    setConnecting(true);
    try {
      const r = await api.post('/vinted/connect', { session_token: tokenInput.trim(), datadome_cookie: "" });
      setConnected(true); setVintedUsername(r.data.username);
      toast.success(`Connesso come ${r.data.username}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Token non valido'); }
    finally { setConnecting(false); }
  };

  const handleDisconnect = async () => {
    const ok = await confirm('Disconnettere Vinted?');
    if (!ok) return;
    try {
      await api.delete('/vinted/disconnect');
      setConnected(false); setVintedUsername(''); setItems([]);
      setTokenInput(''); setTokenExpired(false);
      toast.success('Vinted disconnesso');
    } catch { toast.error('Errore disconnessione'); }
  };

  const filtered = items.filter((item) => {
    if (search && !item.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && item.status !== statusFilter) return false;
    return true;
  });

  if (!connected && !loading) {
    return (
      <Layout>

        {ConfirmDialog}
        <main className="max-w-2xl mx-auto px-4 py-12">
          <div className="card" style={{ padding: 32 }}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${T.violetDark}30` }}>
                <span className="text-3xl">🛍️</span>
              </div>
              <h1 className="page-title">Collega Vinted</h1>
              <p className="mt-2" style={{ color: T.muted }}>Gestisci i tuoi annunci Vinted direttamente dalla dashboard</p>
            </div>
            <div className="rounded-xl p-4 mb-6" style={{ background: T.elem, border: `1px solid ${T.border}` }}>
              <h3 className="font-bold text-sm mb-2" style={{ color: T.violet }}>Come trovare il token:</h3>
              <ol className="text-sm space-y-1 list-decimal list-inside" style={{ color: T.muted }}>
                <li>Vai su <span style={{ color: T.text }}>vinted.it</span> e accedi</li>
                <li>Premi <span style={{ color: T.text, fontFamily: T.fontMono }}>F12</span> → tab <span style={{ color: T.text }}>Application</span></li>
                <li>Cookies → https://www.vinted.it → <span style={{ color: T.violet, fontFamily: T.fontMono }}>access_token_web</span></li>
              </ol>
            </div>
            <div className="space-y-3">
              <input type="password" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Incolla qui il session token..." className="input w-full" />
              <button onClick={handleConnect} disabled={connecting} className="btn-primary w-full">
                {connecting ? 'Collegamento...' : 'Collega Vinted'}
              </button>
            </div>
          </div>
      </main>
    </Layout>
    );
  }

  return (
    <Layout>

      {ConfirmDialog}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tokenExpired && (
          <div className="rounded-xl px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-2" style={{ background: `${T.red}15`, border: `1px solid ${T.red}40` }}>
            <span className="text-sm" style={{ color: T.red }}>Token Vinted scaduto — Disconnetti e ricollegati con un token fresco</span>
            <button onClick={handleDisconnect} className="btn-danger text-xs">Disconnetti</button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="page-title">I miei annunci Vinted</h1>
            <p className="mt-1 flex items-center gap-2" style={{ color: T.muted }}>
              <span className="inline-flex items-center gap-1" style={{ color: T.green }}>
                <span className="dot-live" style={{ background: T.green }}></span>{vintedUsername}
              </span>
              <span style={{ color: T.faint }}>— {totalItems} annunci</span>
              <button onClick={handleDisconnect} className="btn-danger text-xs ml-2" style={{ padding: '2px 8px' }}>Disconnetti</button>
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={loadItems} className="btn-ghost text-xs">Aggiorna</button>
            <select value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value)); setPage(1); }}
              className="input text-sm" style={{ padding: '8px 12px' }}>
              <option value={10}>10 / pagina</option>
              <option value={20}>20 / pagina</option>
              <option value={50}>50 / pagina</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per titolo..."
            className="input flex-1 text-sm" style={{ padding: '8px 16px' }} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="input text-sm" style={{ padding: '8px 12px' }}>
            <option value="">Tutti gli stati</option>
            <option value="active">Attivo</option>
            <option value="hidden">Nascosto</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12" style={{ color: T.faint }}>Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <p className="text-lg" style={{ color: T.muted }}>Nessun annuncio trovato</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    <th className="px-3 py-3 text-left font-bold uppercase" style={{ background: T.elem, fontSize: '0.72rem', color: T.muted }}>Foto</th>
                    <th className="px-3 py-3 text-left font-bold uppercase" style={{ background: T.elem, fontSize: '0.72rem', color: T.muted }}>Titolo</th>
                    <th className="px-3 py-3 text-left font-bold uppercase" style={{ background: T.elem, fontSize: '0.72rem', color: T.muted }}>Stato</th>
                    <th className="px-3 py-3 text-left font-bold uppercase" style={{ background: T.elem, fontSize: '0.72rem', color: T.muted }}>Prezzo</th>
                    <th className="px-3 py-3 text-center font-bold uppercase" style={{ background: T.elem, fontSize: '0.72rem', color: T.muted }}>❤️</th>
                    <th className="px-3 py-3 text-center font-bold uppercase" style={{ background: T.elem, fontSize: '0.72rem', color: T.muted }}>👁️</th>
                    <th className="px-3 py-3 text-left font-bold uppercase" style={{ background: T.elem, fontSize: '0.72rem', color: T.muted }}>Data</th>
                    <th className="px-3 py-3 text-right font-bold uppercase" style={{ background: T.elem, fontSize: '0.72rem', color: T.muted }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr key={item.id} style={{ borderBottom: `1px solid ${T.border}30` }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td className="px-3 py-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden" style={{ background: T.elem }}>
                          {item.photos?.[0]?.url ? <img src={item.photos[0].url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ color: T.faint }}>📷</div>}
                        </div>
                      </td>
                      <td className="px-3 py-3" style={{ fontSize: '0.88rem' }}><a href={item.url} target="_blank" rel="noopener noreferrer" className="font-medium truncate block max-w-[200px]" style={{ color: T.text }} onMouseEnter={e => e.target.style.color = T.violet} onMouseLeave={e => e.target.style.color = T.text}>{item.title}</a></td>
                      <td className="px-3 py-3" style={{ fontSize: '0.88rem' }}>
                        <span className={`badge ${item.status === 'active' ? 'badge-green' : ''}`} style={item.status !== 'active' ? { background: 'rgba(107,114,128,0.2)', color: T.muted } : {}}>
                          {item.status === 'active' ? 'attivo' : 'nascosto'}
                        </span>
                      </td>
                      <td className="px-3 py-3" style={{ fontSize: '0.88rem' }}>
                        <span className="font-bold text-sm" style={{ color: T.violet }}>
                          €{parseFloat(item.price || 0).toFixed(2)}
                        </span>
                        <a href={`https://www.vinted.it/items/${item.id}/edit`} target="_blank" rel="noopener noreferrer"
                          className="ml-1.5 text-xs" style={{ color: T.faint }} title="Modifica su Vinted"
                          onMouseEnter={e => e.target.style.color = T.violet} onMouseLeave={e => e.target.style.color = T.faint}>
                          ✏️
                        </a>
                      </td>
                      <td className="px-3 py-3 text-center text-sm" style={{ color: T.text, fontSize: '0.88rem' }}>{item.favourites_count || 0}</td>
                      <td className="px-3 py-3 text-center text-sm" style={{ color: T.text, fontSize: '0.88rem' }}>{item.views_count || 0}</td>
                      <td className="px-3 py-3 text-xs" style={{ color: T.muted, fontSize: '0.88rem' }}>{item.created_at ? new Date(typeof item.created_at === 'number' ? item.created_at * 1000 : item.created_at).toLocaleDateString('it-IT') : '—'}</td>
                      <td className="px-3 py-3 text-right relative">
                        <button onClick={() => setMenuOpenId(menuOpenId === item.id ? null : item.id)} className="px-2 py-1 rounded" style={{ color: T.muted }} onMouseEnter={e => { e.target.style.color = T.text; e.target.style.background = T.elem; }} onMouseLeave={e => { e.target.style.color = T.muted; e.target.style.background = 'transparent'; }}>···</button>
                        {menuOpenId === item.id && (
                          <div className="absolute right-3 top-full mt-1 rounded-xl shadow-2xl py-1 min-w-[160px] z-40" style={{ background: T.card, border: `1px solid ${T.border}` }}>
                            <a href={item.url} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpenId(null)}
                              className="block px-4 py-2 text-sm" style={{ color: T.text }} onMouseEnter={e => e.target.style.background = `${T.elem}80`} onMouseLeave={e => e.target.style.background = 'transparent'}>Vedi annuncio</a>
                            <a href={`https://www.vinted.it/items/${item.id}/edit`} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpenId(null)}
                              className="block px-4 py-2 text-sm" style={{ color: T.violet }} onMouseEnter={e => e.target.style.background = `${T.elem}80`} onMouseLeave={e => e.target.style.background = 'transparent'}>Modifica su Vinted</a>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: `1px solid ${T.border}` }}>
                <span className="text-sm" style={{ color: T.muted }}>Pagina {page} di {totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost text-sm disabled:opacity-30">← Prec</button>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-ghost text-sm disabled:opacity-30">Succ →</button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>    </Layout>
  );
}
